// ============================================================
// TILT — THEME  ("Neon Cathedral")
// ------------------------------------------------------------
// HARVESTED FROM TERROR: the LAWS, not the colors.
//
//   LAW 1 — TWO NAMESPACES, NEVER MIXED.
//     PF = PLAYFIELD. The table, the ball, the flippers, the
//          lamps. Everything inside the glass.
//     UI = CHROME. The HUD, menus, buttons, panels, shop.
//          Everything outside the glass.
//     A PF color NEVER appears in chrome. A UI color NEVER
//     appears on the playfield. This is what makes the table
//     read as a physical object sitting inside an interface,
//     instead of a flat webpage with a ball on it. Terror
//     learned this the hard way (SD vs GM); TILT is born with it.
//
//   LAW 2 — KEYFRAMES ARE INJECTED, NEVER INLINE.
//     ensureKeyframes() writes a <style> into document.head ONCE,
//     id-guarded. NEVER an inline <style> block in JSX — React
//     remounts it on every render and the animation FREEZES
//     mid-cycle. Terror lost a day to this (the "J'aeonxi foil
//     freeze"). It does not happen twice.
//
//   LAW 3 — NO FLAT FILLS ON CHROME.
//     Every surface has a light source. Gradient, edge light,
//     shadow beneath. Terror's material layer, ported in spirit.
//
// ── THE LOOK ─────────────────────────────────────────────────
// A pinball machine in a cathedral that's been repossessed by
// something electric. Cold blue-black glass. Neon that hums.
// Brass that's seen better centuries. Magenta where it hurts.
// ============================================================

// ── PLAYFIELD — inside the glass ONLY ─────────────────────────
export const PF = {
  deck0:  "#05060f",     // the void beneath the table
  deck1:  "#0a0d1c",     // playfield base
  deck2:  "#111529",     // raised inlay
  deck3:  "#1a2140",     // lit inlay

  wall:      "#2e3a5f",  // plain wall
  wallLit:   "#5570b8",  // wall catching light
  rail:      "#7d8fc4",  // guide rails / ramp edges

  ball:      "#dfe6f5",
  ballCore:  "#ffffff",
  ballShade: "#5c6a8c",

  // Lamp colors — the language of the playfield.
  neon:      "#22d3ee",  // cyan — the default "on"
  neonDim:   "#0e5568",
  ember:     "#fb7185",  // rose — danger, outlanes, drain
  emberDim:  "#6b2534",
  gold:      "#f2c14e",  // brass — targets, jackpots, value
  goldDim:   "#6b5218",
  violet:    "#a855f7",  // arcane — powers, ghosts, magnets
  violetDim: "#4a2570",
  ice:       "#93e6ff",  // freeze
  toxic:     "#84e04a",  // objectives, nodes
  bone:      "#e8e4d8",  // inert plastic

  flipper:   "#f2c14e",
  flipperUp: "#fff2c4",

  fontPF: "'Orbitron', 'Rajdhani', 'Impact', sans-serif",
};

// ── CHROME — everything outside the glass ─────────────────────
export const UI = {
  page:       "#07080f",
  pageDeep:   "#04050a",
  panel:      "#10131f",
  panelLit:   "#1a1f31",
  panelEdge:  "#2c344d",

  brass:      "#c9a24a",
  brassBright:"#f0cf72",
  brassDim:   "#7a6224",

  chrome:     "#aab3c9",
  chromeBright:"#e4ebf7",
  chromeDim:  "#575e73",

  hot:        "#f0407a",   // magenta — the SABOTAGE color
  hotDim:     "#7a1a3d",
  cool:       "#34d9e8",   // cyan — the SELF color
  coolDim:    "#12545e",

  text:       "#e6e9f2",
  textDim:    "#9aa2b8",
  textFaint:  "#5d647a",

  good:       "#5ad48a",
  bad:        "#f0506b",

  font:      "'Rajdhani', 'Segoe UI', system-ui, sans-serif",
  fontD:     "'Orbitron', 'Rajdhani', sans-serif",
  fontNum:   "'JetBrains Mono', 'Consolas', monospace",
};

// Power tint → PF color. Powers name a tint; this resolves it.
// One map, so a power's color is defined ONCE.
export const TINTS = {
  ice:    PF.ice,
  violet: PF.violet,
  gold:   PF.gold,
  teal:   PF.neon,
  silver: PF.bone,
  red:    PF.ember,
  vellum: UI.chrome,
};
export const tintOf = (key) => TINTS[key] || PF.neon;

// ── Material layer (chrome only) ──────────────────────────────
export const BRASS_METAL =
  "linear-gradient(160deg,#f5dd8e 0%,#8a6a20 24%,#e6c463 44%,#5c4614 66%,#f0cf72 86%,#7a6224 100%)";
export const CHROME_METAL =
  "linear-gradient(160deg,#f2f6ff 0%,#666e85 24%,#c4cddf 46%,#454b5e 68%,#dfe6f5 88%,#575e73 100%)";

export const PANEL =
  "linear-gradient(174deg,#1a1f31 0%,#10131f 58%,#0a0c15 100%)";
export const PANEL_LIT =
  "linear-gradient(174deg,#2a3350 0%,#191f33 55%,#0e1220 100%)";
export const RECESS =
  "linear-gradient(174deg,#03040a 0%,#0a0c15 60%,#0e1220 100%)";

export const PAGE_BG = [
  "radial-gradient(ellipse at 50% -10%, rgba(52,217,232,0.10) 0%, transparent 55%)",
  "radial-gradient(ellipse at 82% 92%, rgba(240,64,122,0.07) 0%, transparent 50%)",
  "repeating-linear-gradient(0deg, transparent 0 3px, rgba(255,255,255,0.012) 3px 4px)",
  "#07080f",
].join(", ");

// Faceted geometry — the cut corner. TILT's chrome is machined,
// not rounded. ⚠ clip-path clips box-shadow — use filter:drop-shadow
// for outer depth on faceted elements (inset shadows still render).
export const facet = (n = 10) =>
  `polygon(${n}px 0, calc(100% - ${n}px) 0, 100% ${n}px, 100% calc(100% - ${n}px), calc(100% - ${n}px) 100%, ${n}px 100%, 0 calc(100% - ${n}px), 0 ${n}px)`;
export const facetTB = (n = 8) =>
  `polygon(${n}px 0, 100% 0, 100% calc(100% - ${n}px), calc(100% - ${n}px) 100%, 0 100%, 0 ${n}px)`;

export const RAISED = "inset 0 1px 0 rgba(228,235,247,0.14), 0 3px 10px rgba(0,0,0,0.65)";
export const FLOAT  = "inset 0 1px 0 rgba(240,207,114,0.26), 0 12px 38px rgba(0,0,0,0.8), 0 0 48px rgba(52,217,232,0.10)";
export const SUNK   = "inset 0 2px 9px rgba(0,0,0,0.8), inset 0 -1px 0 rgba(228,235,247,0.05)";

export const bloom = (c = UI.cool) =>
  `0 0 14px ${c}66, 0 0 34px ${c}30, inset 0 0 20px ${c}20`;
export const glow = (c = UI.brassBright) => `0 0 10px ${c}88, 0 1px 2px #000`;
export const SET  = "0 1px 2px rgba(0,0,0,0.9)";

// ══════════════════════════════════════════════════════════════
// KEYFRAMES — LAW 2. Injected once into <head>, id-guarded.
// EVERY animation in TILT lives here. If you find yourself
// writing `<style>` inside a component: stop. Add it here.
// ══════════════════════════════════════════════════════════════
const KF_ID = "tilt-keyframes";

export function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KF_ID)) return;      // ← the id-guard

  const el = document.createElement("style");
  el.id = KF_ID;
  el.textContent = `
@keyframes tilt-pulse {
  0%,100% { opacity: .55; transform: scale(1); }
  50%     { opacity: 1;   transform: scale(1.06); }
}
@keyframes tilt-hum {
  0%,100% { filter: brightness(1)   drop-shadow(0 0 4px currentColor); }
  50%     { filter: brightness(1.4) drop-shadow(0 0 12px currentColor); }
}
@keyframes tilt-sweep {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
}
@keyframes tilt-pop {
  0%   { transform: translate(-50%,-50%) scale(.5); opacity: 0; }
  18%  { transform: translate(-50%,-50%) scale(1.15); opacity: 1; }
  100% { transform: translate(-50%,-140%) scale(1); opacity: 0; }
}
@keyframes tilt-ring {
  0%   { transform: translate(-50%,-50%) scale(.3); opacity: .9; }
  100% { transform: translate(-50%,-50%) scale(2.2); opacity: 0; }
}
@keyframes tilt-shake {
  0%,100% { transform: translate(0,0); }
  20%     { transform: translate(-3px, 2px); }
  40%     { transform: translate(3px, -2px); }
  60%     { transform: translate(-2px,-2px); }
  80%     { transform: translate(2px, 3px); }
}
@keyframes tilt-scan {
  0%   { transform: translateY(-100%); }
  100% { transform: translateY(700%); }
}
@keyframes tilt-charge {
  0%,100% { box-shadow: 0 0 8px ${UI.cool}66; }
  50%     { box-shadow: 0 0 22px ${UI.cool}cc, 0 0 40px ${UI.cool}55; }
}
@keyframes tilt-danger {
  0%,100% { background: ${UI.hotDim}; }
  50%     { background: ${UI.hot}; }
}
@keyframes tilt-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes tilt-float {
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-5px); }
}
@keyframes tilt-flicker {
  0%,100%   { opacity: 1; }
  92%       { opacity: 1; }
  94%       { opacity: .3; }
  96%       { opacity: 1; }
  98%       { opacity: .5; }
}
@keyframes tilt-pop-in {
  0%   { opacity: 0; transform: translateX(-7px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes tilt-breathe {
  0%,100% { opacity: .45; }
  50%     { opacity: 1; }
}
`;
  document.head.appendChild(el);
}

// ── THE EMPIRE KIT's palette (2026-10-25, the Mark): GM + MANUSCRIPT_BG, VERBATIM from the Pier's theme.js.
// Only the shared kit reads these (AchievementBadgeBook); Tilt's own chrome stays on UI/PF above.
export const GM = {
  // Page backgrounds — aged vellum, dark ink-stained
  page:          "#0f0c07",       // deepest ink-soaked vellum
  pageWarm:      "#1a1509",       // aged leather cover
  pageMid:       "#241c0c",       // worn parchment mid
  pageLight:     "#2e2410",       // candlelit page
  pageBorder:    "#3d2e14",       // manuscript margin line

  // Ink colors
  inkGold:       "#c8963c",       // iron gall gold — primary accent
  inkGoldBright: "#e8b84b",       // fresh gold illumination
  inkGoldDim:    "#8a6520",       // faded annotation
  inkBrown:      "#8a6a30",       // worn copper border
  inkRed:        "#8b2020",       // vermillion rubric

  // Parchment text
  vellum:        "#e8d5a3",       // warm candlelight cream — main text
  vellumDim:     "#b89a5a",       // aged gold ink — secondary
  vellumFaint:   "#7a6040",       // water-damaged — muted

  // Decorative
  ruling:        "rgba(139,106,48,0.18)", // faint ruling lines

  font:  "'Cinzel', 'Palatino Linotype', serif",
  fontD: "'Cinzel Decorative', 'Palatino Linotype', serif",
};

export const MANUSCRIPT_BG = [
  "repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(139,106,48,0.05) 27px, rgba(139,106,48,0.05) 28px)",
  "radial-gradient(ellipse at 25% 15%, rgba(120,80,20,0.18) 0%, transparent 55%)",
  "radial-gradient(ellipse at 75% 85%, rgba(100,60,15,0.15) 0%, transparent 55%)",
  "#0f0c07",
].join(", ");

export default { PF, UI, ensureKeyframes };
